import supabase from './supabase';
import usageService from './usageService';

class SubscriptionService {
  /**
   * Get all available subscription plans
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  async getAvailablePlans() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) {
        console.error('Error getting plans:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data.map(plan => ({
          id: plan.id,
          name: plan.name,
          description: plan.description,
          monthlyPrice: plan.price_monthly,
          yearlyPrice: plan.price_yearly,
          monthlyLimit: plan.monthly_review_limit,
          isUnlimited: plan.monthly_review_limit === null,
          stripePriceIdMonthly: plan.stripe_price_id_monthly,
          stripePriceIdYearly: plan.stripe_price_id_yearly,
          isFreePlan: plan.name === 'free',
          isProPlan: plan.name === 'pro'
        }))
      };
    } catch (error) {
      console.error('Error in getAvailablePlans:', error);
      return { success: false, error: 'Failed to get subscription plans' };
    }
  }

  /**
   * Get organization's current subscription
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getOrganizationSubscription(organizationId) {
    try {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        // If no subscription exists, they're on the default free plan
        if (error.code === 'PGRST116') {
          const freePlanResult = await this.getFreePlan();
          if (freePlanResult.success) {
            return {
              success: true,
              data: {
                subscription: null,
                plan: freePlanResult.data,
                isFreePlan: true,
                isProPlan: false,
                status: 'active',
                billingCycle: null,
                stripeSubscriptionId: null,
                currentPeriodEnd: null
              }
            };
          }
          return freePlanResult;
        }
        
        console.error('Error getting subscription:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: {
          subscription: data,
          plan: data.plan,
          isFreePlan: data.plan?.name === 'free',
          isProPlan: data.plan?.name === 'pro',
          status: data.status,
          billingCycle: data.billing_cycle,
          stripeSubscriptionId: data.stripe_subscription_id,
          stripeCustomerId: data.stripe_customer_id,
          currentPeriodStart: data.current_period_start,
          currentPeriodEnd: data.current_period_end
        }
      };
    } catch (error) {
      console.error('Error in getOrganizationSubscription:', error);
      return { success: false, error: 'Failed to get subscription' };
    }
  }

  /**
   * Get the free plan details
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getFreePlan() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('name', 'free')
        .single();

      if (error) {
        console.error('Error getting free plan:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getFreePlan:', error);
      return { success: false, error: 'Failed to get free plan' };
    }
  }

  /**
   * Get the pro plan details
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getProPlan() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('name', 'pro')
        .single();

      if (error) {
        console.error('Error getting pro plan:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in getProPlan:', error);
      return { success: false, error: 'Failed to get pro plan' };
    }
  }

  /**
   * Create or update organization subscription (for Stripe webhook handling)
   * @param {string} organizationId - The organization ID
   * @param {object} subscriptionData - Subscription data from Stripe
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async upsertSubscription(organizationId, subscriptionData) {
    try {
      const {
        planId,
        stripeSubscriptionId,
        stripeCustomerId,
        status,
        currentPeriodStart,
        currentPeriodEnd,
        billingCycle = 'monthly'
      } = subscriptionData;

      const { data, error } = await supabase
        .from('organization_subscriptions')
        .upsert({
          organization_id: organizationId,
          plan_id: planId,
          stripe_subscription_id: stripeSubscriptionId,
          stripe_customer_id: stripeCustomerId,
          status,
          current_period_start: currentPeriodStart,
          current_period_end: currentPeriodEnd,
          billing_cycle: billingCycle,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'organization_id'
        })
        .select()
        .single();

      if (error) {
        console.error('Error upserting subscription:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in upsertSubscription:', error);
      return { success: false, error: 'Failed to update subscription' };
    }
  }

  /**
   * Update subscription status (for webhook handling)
   * @param {string} stripeSubscriptionId - Stripe subscription ID
   * @param {string} status - New status
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async updateSubscriptionStatus(stripeSubscriptionId, status) {
    try {
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('stripe_subscription_id', stripeSubscriptionId)
        .select()
        .single();

      if (error) {
        console.error('Error updating subscription status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateSubscriptionStatus:', error);
      return { success: false, error: 'Failed to update subscription status' };
    }
  }

  /**
   * Cancel organization subscription (downgrade to free)
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async downgradeToFree(organizationId) {
    try {
      // Get free plan
      const freePlanResult = await this.getFreePlan();
      if (!freePlanResult.success) {
        return freePlanResult;
      }

      // Update subscription to free plan
      const { data, error } = await supabase
        .from('organization_subscriptions')
        .update({
          plan_id: freePlanResult.data.id,
          status: 'active',
          stripe_subscription_id: null,
          stripe_customer_id: null,
          current_period_start: null,
          current_period_end: null,
          billing_cycle: null,
          updated_at: new Date().toISOString()
        })
        .eq('organization_id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Error downgrading to free:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in downgradeToFree:', error);
      return { success: false, error: 'Failed to downgrade to free plan' };
    }
  }

  /**
   * Get subscription summary for billing UI
   * @param {string} organizationId - The organization ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getSubscriptionSummary(organizationId) {
    try {
      const subscriptionResult = await this.getOrganizationSubscription(organizationId);
      if (!subscriptionResult.success) {
        return subscriptionResult;
      }

      const { plan, status, billingCycle, currentPeriodEnd } = subscriptionResult.data;

      // Get current usage
      const usageResult = await usageService.getCurrentMonthUsage(organizationId);
      
      const usage = usageResult.success ? usageResult.data : null;

      return {
        success: true,
        data: {
          plan: {
            name: plan.name,
            description: plan.description,
            monthlyPrice: plan.price_monthly,
            yearlyPrice: plan.price_yearly,
            isFreePlan: plan.name === 'free',
            isProPlan: plan.name === 'pro'
          },
          subscription: {
            status,
            billingCycle,
            currentPeriodEnd,
            isActive: status === 'active'
          },
          usage: usage ? {
            used: usage.used,
            limit: usage.limit,
            isUnlimited: usage.isUnlimited,
            canGenerate: usage.canGenerate,
            display: this.formatUsageDisplay(usage),
            needsUpgrade: this.shouldShowUpgradePrompt(usage)
          } : null
        }
      };
    } catch (error) {
      console.error('Error in getSubscriptionSummary:', error);
      return { success: false, error: 'Failed to get subscription summary' };
    }
  }

  /**
   * Format usage for display
   * @param {object} usage - Usage data
   * @returns {string} Formatted usage string
   */
  formatUsageDisplay(usage) {
    if (usage.isUnlimited) {
      return `${usage.used} AI reviews used (Unlimited)`;
    }
    return `${usage.used}/${usage.limit} AI reviews used this month`;
  }

  /**
   * Check if should show upgrade prompt
   * @param {object} usage - Usage data
   * @returns {boolean} True if should show upgrade prompt
   */
  shouldShowUpgradePrompt(usage) {
    if (usage.isUnlimited) return false;
    return usage.limit > 0 && usage.used >= (usage.limit * 0.8);
  }

  /**
   * Get plan comparison data for upgrade/downgrade UI
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async getPlanComparison() {
    try {
      const plansResult = await this.getAvailablePlans();
      if (!plansResult.success) {
        return plansResult;
      }

      const plans = plansResult.data;
      const freePlan = plans.find(p => p.name === 'free');
      const proPlan = plans.find(p => p.name === 'pro');

      return {
        success: true,
        data: {
          free: freePlan,
          pro: proPlan,
          comparison: {
            features: {
              ai_reviews: {
                free: `${freePlan?.monthlyLimit || 2} per month`,
                pro: 'Unlimited'
              },
              support: {
                free: 'Email support',
                pro: 'Priority support'
              },
              integrations: {
                free: 'All integrations',
                pro: 'All integrations + Custom'
              }
            },
            pricing: {
              free: { monthly: 0, yearly: 0 },
              pro: { 
                monthly: proPlan?.monthlyPrice || 97, 
                yearly: proPlan?.yearlyPrice || 597 
              }
            }
          }
        }
      };
    } catch (error) {
      console.error('Error in getPlanComparison:', error);
      return { success: false, error: 'Failed to get plan comparison' };
    }
  }
}

export default new SubscriptionService();